import { useCallback, useMemo, useRef, useState } from 'react';

import { Platform } from 'react-native';

import { useDispatch, useSelector } from 'react-redux';

import { setDeleteActionPinValidationTime } from 'store/recordings/actions.ts';
import { selectActiveUserProfile } from 'store/user/selectors.ts';

import { UseRecordingsHookReturnType } from 'utils/hooks/useRecordings';
import { isTablet } from 'utils/responsiveness.ts';

import {
    ManageRecordingItem,
    ManageRecordingItemKeys,
    PinDialogType,
} from 'screens/Detail/types';

import { PinDialogRef } from 'components/PinDialog';
import usePinDialogStatus from 'components/PinDialog/hooks/usePinDialogStatus';

type Props = {
    handleOpenDetails?: () => void;
    handleDeleteRecording: UseRecordingsHookReturnType['handleDeleteRecording'];
    handleUnprotectRecording: UseRecordingsHookReturnType['handleUnprotectRecording'];
    onManageRecordingPress: UseRecordingsHookReturnType['onManageRecordingPress'];
    handleDeleteSeriesRecording: UseRecordingsHookReturnType['handleDeleteSeriesRecording'];
};

const useRecordingsNotificationsAndOverlays = ({
    handleOpenDetails,
    handleDeleteRecording,
    handleUnprotectRecording,
    onManageRecordingPress,
    handleDeleteSeriesRecording,
}: Props) => {
    const dispatch = useDispatch();

    const selectedProfileId = useSelector(selectActiveUserProfile);

    const pinDialogRef = useRef<PinDialogRef | null>(null);

    const [canCloseButtonDropdown, setCanCloseButtonDropdown] = useState(false);
    const [pinDialogType, setPinDialogType] = useState<PinDialogType>();
    const [showPinDialog, setShowPinDialog] = useState(false);

    const handleClosePinDialogOnVitalError = useCallback(() => {
        pinDialogRef.current?.closeKeyboard();
        setShowPinDialog(false);
    }, []);

    const { pinDialogStatus, onResetPinStatus, onValidatePin } =
        usePinDialogStatus(
            pinDialogRef,
            showPinDialog,
            handleClosePinDialogOnVitalError,
        );

    const handleShowPinDialog = useCallback((pinType: PinDialogType) => {
        setPinDialogType(pinType);
        setShowPinDialog(true);
    }, []);

    const handlePinDialogSubmit = useCallback(
        (pin: string) => {
            onValidatePin({
                pin,
                onPinValid: () => {
                    if (pinDialogType === PinDialogType.DELETE) {
                        if (selectedProfileId) {
                            dispatch(
                                setDeleteActionPinValidationTime(
                                    selectedProfileId,
                                    Date.now(),
                                ),
                            );
                        }

                        handleDeleteRecording(pin);
                        handleOpenDetails?.();
                    } else if (pinDialogType === PinDialogType.DELETE_SERIES) {
                        if (selectedProfileId) {
                            dispatch(
                                setDeleteActionPinValidationTime(
                                    selectedProfileId,
                                    Date.now(),
                                ),
                            );
                        }

                        handleDeleteSeriesRecording(pin);
                        handleOpenDetails?.();
                    } else {
                        handleUnprotectRecording(pin);
                    }

                    onResetPinStatus();
                    setShowPinDialog(false);
                    setTimeout(() => {
                        setCanCloseButtonDropdown(true);
                    }, 100);
                },
            });
        },
        [
            handleOpenDetails,
            onValidatePin,
            pinDialogType,
            onResetPinStatus,
            handleDeleteRecording,
            handleDeleteSeriesRecording,
            handleUnprotectRecording,
            selectedProfileId,
        ],
    );

    const handlePinDialogClose = useCallback(() => {
        pinDialogRef.current?.closeKeyboard();
        setShowPinDialog(false);
        onResetPinStatus();
    }, [pinDialogRef, onResetPinStatus]);

    const onManageRecordingPressCallback = useCallback(
        (item: ManageRecordingItem) => {
            // Unprotect PIN action requires PIN dialog to be shown.
            // As far as on tablet devices DetailPageButtons
            // is a Modal PickerList component - PinDialog must be shown after PickerList would be hidden,
            // otherwise IOS won't be able to show two Modal components in the same View.
            if (
                Platform.OS === 'ios' &&
                isTablet &&
                item.key === ManageRecordingItemKeys.UnprotectRecording
            ) {
                onManageRecordingPress(
                    item,
                    setCanCloseButtonDropdown,
                    (pinType: PinDialogType) => {
                        setTimeout(() => {
                            handleShowPinDialog(pinType);
                        }, 50);
                    },
                );
            } else {
                onManageRecordingPress(
                    item,
                    setCanCloseButtonDropdown,
                    handleShowPinDialog,
                );
            }
        },
        [
            onManageRecordingPress,
            setCanCloseButtonDropdown,
            handleShowPinDialog,
        ],
    );

    return useMemo(
        () => ({
            canCloseButtonDropdown,
            onManageRecordingPressCallback,
            handleShowPinDialog,

            pinDialogProps: {
                pinDialogRef,
                showPinDialog,
                handlePinDialogClose,
                handlePinDialogSubmit,
                pinDialogStatus,
            },
        }),
        [
            canCloseButtonDropdown,
            onManageRecordingPressCallback,
            handleShowPinDialog,
            showPinDialog,
            handlePinDialogClose,
            handlePinDialogSubmit,
            pinDialogStatus,
        ],
    );
};

export default useRecordingsNotificationsAndOverlays;
